<script>
  /**
   * Where inbound guest messages went, and what is failing.
   *
   * Read-only by design: this is where you find out what is wrong, not where
   * you fix it. Raw webhook payloads are never fetched — they contain guest
   * message text and contact details, and the server does not return them.
   */
  import api from '$lib/api.js';
  import { authStore } from '../stores/authStore.svelte.js';
  import PageHeader from '../components/PageHeader.svelte';
  import DataTable from '../components/DataTable.svelte';
  import Pagination from '../components/Pagination.svelte';
  import EmptyState from '../components/EmptyState.svelte';

  let tab = $state('overview');
  let days = $state(7);
  let loading = $state(true);
  let error = $state(null);
  let health = $state(null);

  let logPage = $state(1);
  let problemsOnly = $state(true);
  let log = $state(null);

  let jobPage = $state(1);
  let failedOnly = $state(true);
  let jobs = $state(null);

  const tenantId = $derived(authStore.tenantId ?? undefined);

  /** Statuses that mean a guest message did not get answered. */
  const BAD = new Set(['failed', 'abandoned_unsynced']);

  async function loadHealth() {
    loading = true;
    error = null;
    try {
      health = await api.getPipelineHealth({ tenant_id: tenantId, days });
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  }

  async function loadLog() {
    log = await api.listProcessingLog({
      tenant_id: tenantId, days, page: logPage, limit: 25,
      problems_only: problemsOnly || undefined,
    });
  }

  async function loadJobs() {
    jobs = await api.listSyncJobs({
      tenant_id: tenantId, days, page: jobPage, limit: 25,
      failed_only: failedOnly || undefined,
    });
  }

  $effect(() => { tenantId; days; loadHealth(); });
  $effect(() => { if (tab === 'messages') { tenantId; days; logPage; problemsOnly; loadLog(); } });
  $effect(() => { if (tab === 'syncs') { tenantId; days; jobPage; failedOnly; loadJobs(); } });

  function when(v) { return v ? new Date(v).toLocaleString() : '—'; }
  function ms(v) { return v == null ? '—' : `${v} ms`; }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'messages', label: 'Message processing' },
    { id: 'syncs', label: 'Sync jobs' },
  ];

  const logColumns = [
    { key: 'created_at', label: 'When', render: (r) => when(r.created_at) },
    {
      key: 'processing_status', label: 'Outcome',
      render: (r) => r.status_label ?? r.processing_status ?? '—',
    },
    {
      key: 'confidence_score', label: 'Confidence', align: 'right',
      render: (r) => (r.confidence_score == null ? '—' : `${Math.round(r.confidence_score * 100)}%`),
    },
    { key: 'processing_time_ms', label: 'Took', align: 'right', render: (r) => ms(r.processing_time_ms) },
    { key: 'retry_count', label: 'Retries', align: 'right', render: (r) => String(r.retry_count ?? 0) },
    { key: 'error_message', label: 'Error', render: (r) => r.error_message ?? '' },
  ];

  const jobColumns = [
    { key: 'started_at', label: 'Started', render: (r) => when(r.started_at) },
    { key: 'job_type', label: 'Job' },
    { key: 'status', label: 'Status' },
    {
      key: 'records_processed', label: 'Records', align: 'right',
      render: (r) => String(r.records_processed ?? '—'),
    },
    { key: 'error_message', label: 'Error', render: (r) => r.error_message ?? '' },
  ];
</script>

<PageHeader title="Pipeline health"
            subtitle="What happened to inbound guest messages, and what is failing.">
  {#snippet children()}
    <select class="p-input w-auto" bind:value={days} aria-label="Time window">
      <option value={1}>Last 24 hours</option>
      <option value={7}>Last 7 days</option>
      <option value={30}>Last 30 days</option>
      <option value={90}>Last 90 days</option>
    </select>
  {/snippet}
</PageHeader>

<div class="mb-4 flex gap-1 border-b" style="border-color: var(--p-border)" role="tablist">
  {#each tabs as t}
    <button type="button" role="tab" aria-selected={tab === t.id}
            class="-mb-px border-b-2 px-3 py-2 text-sm"
            style={tab === t.id
              ? 'border-color: var(--p-brand); color: var(--p-brand); font-weight: 600'
              : 'border-color: transparent; color: var(--p-text-muted)'}
            onclick={() => (tab = t.id)}>
      {t.label}
    </button>
  {/each}
</div>

{#if error}
  <p class="p-card p-4 text-sm" style="color: var(--p-danger)">{error}</p>
{:else if loading && !health}
  <p class="p-card p-6 text-sm p-muted">Loading…</p>
{:else if health}
  {#if tab === 'overview'}
    <section class="p-card mb-4 p-4">
      <h2 class="text-sm font-semibold">
        {health.total_processed.toLocaleString()} messages processed
        <span class="p-faint font-normal">· last {health.window_days} days</span>
      </h2>

      {#if !health.total_processed}
        <p class="mt-3 text-sm p-muted">Nothing processed in this window.</p>
      {:else}
        <div class="mt-4 space-y-2">
          {#each health.statuses as s}
            <div>
              <div class="flex items-baseline justify-between gap-3 text-sm">
                <span>
                  {s.label}
                  <span class="p-faint">· {s.status}</span>
                </span>
                <span class="tabular-nums p-faint">
                  {s.count.toLocaleString()} ({s.percent}%)
                </span>
              </div>
              <div class="mt-1 h-2 w-full overflow-hidden rounded-full"
                   style="background-color: var(--p-bg-subtle, rgba(120,120,140,.12))">
                <div class="h-full rounded-full"
                     style="width: {s.percent}%; background-color: {BAD.has(s.status)
                       ? 'var(--p-danger)' : 'var(--p-brand)'}"></div>
              </div>
            </div>
          {/each}
        </div>
      {/if}
    </section>

    <div class="grid gap-4 md:grid-cols-2">
      <section class="p-card p-4">
        <h2 class="text-sm font-semibold">Hostaway sync jobs</h2>
        {#if !health.sync_jobs.length}
          <p class="mt-3 text-sm p-muted">No sync runs in this window.</p>
        {:else}
          <table class="mt-3 w-full text-sm">
            <thead>
              <tr class="text-xs p-faint">
                <th class="text-left font-medium">Job</th>
                <th class="text-right font-medium">OK</th>
                <th class="text-right font-medium">Failed</th>
                <th class="text-left font-medium">Last run</th>
              </tr>
            </thead>
            <tbody>
              {#each health.sync_jobs as j}
                <tr>
                  <td class="py-1">{j.job_type}</td>
                  <td class="py-1 text-right tabular-nums">{j.completed}</td>
                  <td class="py-1 text-right tabular-nums"
                      style={j.failed ? 'color: var(--p-danger); font-weight: 600' : ''}>
                    {j.failed}
                  </td>
                  <td class="py-1 text-xs p-faint">{when(j.last_completed_at)}</td>
                </tr>
                {#if j.last_error}
                  <tr>
                    <td colspan="4" class="pb-2 text-xs" style="color: var(--p-danger)">
                      {j.last_error}
                    </td>
                  </tr>
                {/if}
              {/each}
            </tbody>
          </table>
        {/if}
      </section>

      <section class="p-card p-4">
        <h2 class="text-sm font-semibold">Host notifications</h2>
        {#if !health.notifications.length}
          <p class="mt-3 text-sm p-muted">No notifications sent in this window.</p>
        {:else}
          <ul class="mt-3 space-y-1 text-sm">
            {#each health.notifications as n}
              <li class="flex justify-between">
                <span>{n.channel}</span>
                <span class="tabular-nums p-faint">{n.sent} sent · {n.replied} replied</span>
              </li>
            {/each}
          </ul>
        {/if}
        {#if health.raw_webhook_events === 0}
          <p class="mt-4 text-xs p-faint">
            No raw webhook events are being recorded — nothing writes that table today.
          </p>
        {/if}
      </section>
    </div>

  {:else if tab === 'messages'}
    <label class="mb-3 flex items-center gap-2 text-xs p-muted">
      <input type="checkbox" bind:checked={problemsOnly} />
      Only messages that failed or were dropped
    </label>
    {#if log && !log.items.length}
      <EmptyState icon="fa-circle-check" title="Nothing to show"
                  message={problemsOnly
                    ? 'No failures or drops in this window.'
                    : 'No messages processed in this window.'} />
    {:else if log}
      <DataTable columns={logColumns} rows={log.items} />
      <Pagination pagination={log.pagination} onPage={(p) => (logPage = p)} />
    {/if}

  {:else}
    <label class="mb-3 flex items-center gap-2 text-xs p-muted">
      <input type="checkbox" bind:checked={failedOnly} />
      Only failed runs
    </label>
    {#if jobs && !jobs.items.length}
      <EmptyState icon="fa-circle-check" title="Nothing to show"
                  message={failedOnly ? 'No failed sync runs in this window.' : 'No sync runs.'} />
    {:else if jobs}
      <DataTable columns={jobColumns} rows={jobs.items} />
      <Pagination pagination={jobs.pagination} onPage={(p) => (jobPage = p)} />
    {/if}
  {/if}
{/if}
